import React, { useState, useEffect } from "react"
import * as XLSX from 'xlsx';
import { useFormik } from "formik"
import * as Yup from "yup"
import Dropzone from "react-dropzone"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import { scrollToView, showToaster } from "components/Common/common";
import { useSelector } from "react-redux";
import { PAGECODE } from "components/Common/Constant";
import { ApiNames } from "components/Common/ApiManagement/apiServices";
import axiosInstance from "components/Common/ApiManagement/axiosInstance";
import { ModernButton, ModernCard, ModernInput } from '../../components/ModernUI';
import {
  Globe,
  Upload,
  FileSpreadsheet,
  PlayCircle,
  Link as LinkIcon,
  Server,
  AlertCircle,
  CheckCircle2,
  Loader2
} from 'lucide-react';

const StartAScan = props => {
  document.title = "Start A Scan | GOVCOM"

  const [bulkUrls, setBulkUrls] = useState({})
  const [activeTab, setActiveTab] = useState('single');
  const [selectedFiles, setselectedFiles] = useState([])
  const [isScanning, setIsScanning] = useState(false);

  const user = useSelector((state) => state?.users?.users);
  const hasWritePermission17 = user?.permissions?.write?.includes(PAGECODE["Start A Scan"]);

  const valSchema = Yup.object().shape({
    ["Url Address"]: Yup.string()
      .required("Url Address is required"),
    ["Parent Website"]: Yup.string()
      .required("Parent Website is required"),
    Environment: Yup.string()
      .oneOf(["dev", "qa", "uat", "prod"], "Environment must be either prod, dev, qa or uat")
      .required("Environment is required"),
  });

  const urlSchema = Yup.array().of(valSchema);

  // Helper function to strip http://, https://, and trailing slashes from URLs
  const stripProtocol = (url) => {
    if (!url) return url;
    return url
      .trim()                        // Remove whitespace first
      .replace(/^https?:\/\//, '')   // Remove protocol only
      .replace(/\/+$/, '');          // Remove trailing slashes
  };

  const handleDoc = async (file) => {
    if (file) {
      const reader = new FileReader()

      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        const worksheet = workbook.Sheets[workbook.SheetNames[0]]
        const json = XLSX.utils.sheet_to_json(worksheet)
        try {
          await urlSchema.validate(json, { abortEarly: false })
        } catch (err) {
          console.log(err.errors);
          showToaster(0, err.errors[0])
          setBulkUrls({})
          return
        }
        let obj = {}
        for (let i = 0; i < json.length; i++) {
          const {
            Environment,
            ["Url Address"]: urlAddress,
            ["Parent Website"]: parentWebsite
          } = json[i]

          // Strip protocol from URLs
          const cleanedUrlAddress = stripProtocol(urlAddress);
          const cleanedParentWebsite = stripProtocol(parentWebsite);

          obj[Environment]
            ? obj[Environment].push({ urlAddress: cleanedUrlAddress, parentWebsite: cleanedParentWebsite })
            : obj[Environment] = [{ urlAddress: cleanedUrlAddress, parentWebsite: cleanedParentWebsite }]
        }
        setBulkUrls(obj)
        scrollToView('url-environment')
      }

      reader.readAsArrayBuffer(file)
    }
  }

  const checkURLValidity = (data) => {
    if (!data.urlAddress.match(/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/)) {
      showToaster(0, `Invalid URL address: ${data.urlAddress}`)
      return 0
    }
    if (!data.parentWebsite.match(/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/)) {
      showToaster(0, `Invalid parent web URL: ${data.parentWebsite}`)
      return 0
    }
    return 1
  }

  const startScanAsynchronously = async (url) => {
    try {
      setIsScanning(true);
      const response = await axiosInstance.post(ApiNames.START_SCAN + "?url=" + encodeURIComponent(url));
      if (response.status === 200) {
        showToaster(1, "Scan started successfully!")
      } else {
        showToaster(0, "Failed to start scan.")
      }
    } catch (error) {
      console.error("Error starting scan:", error);
      const errorMessage = error?.response?.data?.error
        || error?.response?.data?.message
        || error?.message
        || "Please check if the API server is running and the URL is valid.";
      showToaster(0, "An error occurred while starting the scan.\n" + errorMessage)
    } finally {
      setIsScanning(false);
    }
  }

  const startBulkScanAsynchronously = async (workbook) => {
    try {
      setIsScanning(true);
      const response = await axiosInstance.post(ApiNames.START_BULK_SCAN, workbook);
      console.log("Bulk scan response:", response.data);
      if (response.status === 200) {
        showToaster(1, "Bulk scan started successfully!")
      } else {
        showToaster(0, "Failed to start bulk scan.")
      }
    } catch (error) {
      console.error("Error starting bulk scan:", error);
      const errorMessage = error?.response?.data?.error
        || error?.response?.data?.message
        || error?.message
        || "Please check if the API server is running and the file format is valid.";
      showToaster(0, "An error occurred while starting the bulk scan.\n" + errorMessage)
    } finally {
      setIsScanning(false);
    }
  }

  const startScanningUrl = (data) => {
    if (!checkURLValidity(data)) return;
    startScanAsynchronously(data.urlAddress);
  }

  const startScanningBulkUrls = () => {
    if (bulkUrls == {} || Object.keys(bulkUrls).length === 0) {
      showToaster(0, "No URLs to scan. Please upload a valid file.")
      return;
    }
    const workbook = generateBulkUrlsData();
    console.log("Workbook: ", workbook);
    if (workbook === -1) return;
    startBulkScanAsynchronously(workbook);
  }

  const generateBulkUrlsData = () => {
    console.log("Bulk URLs: ", bulkUrls);
    const arrayOfURLs = [].concat(bulkUrls['prod'] ?? [], bulkUrls['dev'] ?? [], bulkUrls['qa'] ?? [], bulkUrls['uat'] ?? []);
    console.log("JSON Data: ", arrayOfURLs);
    let invalidURLExists = false;
    arrayOfURLs.forEach((item) => {
      if (!checkURLValidity(item)) invalidURLExists = true;
    });
    if (invalidURLExists) return -1;
    return arrayOfURLs;
  }

  useEffect(() => {
    props.setBreadcrumbItems("Start a Scan", [])
  })

  const handleAcceptedFiles = files => {
    handleDoc(files[0])
    files.map(file =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      }),
    )
    setselectedFiles(files)
  }

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
  }

  const validationSchema = Yup.object({
    URLaddress1: Yup.string().required("URL address is required"),
    parentWebsite: Yup.string().required("Parent website is required"),
    environment: Yup.string()
      .oneOf(["dev", "qa", "uat", "prod"], "Environment must be either prod, dev, qa or uat")
      .required("Environment is required"),
  })

  const validation = useFormik({
    initialValues: {
      URLaddress1: "",
      parentWebsite: "",
      environment: "",
    },
    validationSchema,
    onSubmit: async values => {
      const { environment, ...other } = values

      // Strip protocol from URLs before submitting
      const cleanedUrlAddress = stripProtocol(other.URLaddress1);
      const cleanedParentWebsite = stripProtocol(other.parentWebsite);

      const bulkData = {
        [environment]: [{
          urlAddress: cleanedUrlAddress,
          parentWebsite: cleanedParentWebsite
        }]
      }
      setBulkUrls(bulkData)
      scrollToView('url-environment')
      startScanningUrl(bulkData[environment][0])
    },
  })

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50" style={{ margin: '-2.5rem', marginTop: '-6rem', padding: '2rem', paddingTop: '4rem' }}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center">
              <PlayCircle className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Start a Scan</h1>
          <p className="text-gray-600">Scan a single URL or upload multiple URLs for bulk scanning</p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={() => setActiveTab('single')}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${activeTab === 'single'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
            >
              <div className="flex items-center space-x-2">
                <Globe className="w-5 h-5" />
                <span>Single Scan</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('bulk')}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${activeTab === 'bulk'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
            >
              <div className="flex items-center space-x-2">
                <FileSpreadsheet className="w-5 h-5" />
                <span>Bulk Scan</span>
              </div>
            </button>
          </div>
        </div>

        {/* Single Scan Tab */}
        {activeTab === 'single' && (
          <ModernCard>
            <form onSubmit={validation.handleSubmit} className="space-y-6">
              <div className="space-y-6">
                {/* URL Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center space-x-2">
                      <LinkIcon className="w-4 h-4" />
                      <span>URL Address *</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    name="URLaddress1"
                    value={validation.values.URLaddress1}
                    placeholder="https://example.gov"
                    autoComplete="off"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    className={`w-full px-4 py-3 border ${validation.touched.URLaddress1 && validation.errors.URLaddress1
                        ? 'border-red-300'
                        : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
                  />
                  {validation.touched.URLaddress1 && validation.errors.URLaddress1 && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {validation.errors.URLaddress1}
                    </p>
                  )}
                </div>

                {/* Parent Website */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center space-x-2">
                      <Server className="w-4 h-4" />
                      <span>Parent Website *</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    name="parentWebsite"
                    value={validation.values.parentWebsite}
                    placeholder="https://parent-site.gov"
                    autoComplete="off"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    className={`w-full px-4 py-3 border ${validation.touched.parentWebsite && validation.errors.parentWebsite
                        ? 'border-red-300'
                        : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
                  />
                  {validation.touched.parentWebsite && validation.errors.parentWebsite && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {validation.errors.parentWebsite}
                    </p>
                  )}
                </div>

                {/* Environment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Environment *
                  </label>
                  <select
                    name="environment"
                    value={validation.values.environment}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    className={`w-full px-4 py-3 border ${validation.touched.environment && validation.errors.environment
                        ? 'border-red-300'
                        : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
                  >
                    <option value="">Select environment...</option>
                    <option value="prod">Production</option>
                    <option value="dev">Development</option>
                    <option value="qa">Q/A Test</option>
                    <option value="uat">User Acceptance Testing</option>
                  </select>
                  {validation.touched.environment && validation.errors.environment && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {validation.errors.environment}
                    </p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-center pt-4">
                {hasWritePermission17 ? (
                  <ModernButton
                    type="submit"
                    variant="primary"
                    size="lg"
                    icon={PlayCircle}
                    loading={isScanning}
                  >
                    Start Scan
                  </ModernButton>
                ) : (
                  <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                    🔒 You have read-only access. Contact your admin to enable scanning.
                  </p>
                )}
              </div>
            </form>
          </ModernCard>
        )}

        {/* Bulk Scan Tab */}
        {activeTab === 'bulk' && (
          <ModernCard>
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Excel File</h3>
                <p className="text-sm text-gray-600">
                  Upload an Excel file with columns: Url Address, Parent Website, and Environment
                </p>
              </div>

              <Dropzone
                onDrop={acceptedFiles => {
                  handleAcceptedFiles(acceptedFiles)
                }}
                accept={{
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                  'application/vnd.ms-excel': ['.xls']
                }}
              >
                {({ getRootProps, getInputProps }) => (
                  <div
                    {...getRootProps()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all"
                  >
                    <input {...getInputProps()} />
                    <div className="flex flex-col items-center">
                      <Upload className="w-16 h-16 text-gray-400 mb-4" />
                      <h4 className="text-lg font-medium text-gray-700 mb-2">
                        Drop files here or click to upload
                      </h4>
                      <p className="text-sm text-gray-500">
                        Supported formats: .xlsx, .xls
                      </p>
                    </div>
                  </div>
                )}
              </Dropzone>

              {/* File Preview */}
              {selectedFiles.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">Uploaded Files</h4>
                  {selectedFiles.map((f, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center space-x-3">
                        <FileSpreadsheet className="w-8 h-8 text-green-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{f.name}</p>
                          <p className="text-xs text-gray-500">{f.formattedSize}</p>
                        </div>
                      </div>
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                  ))}
                </div>
              )}

              {/* URLs Summary */}
              {Object.keys(bulkUrls).length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-3">URLs to Scan</h4>
                  <div className="space-y-2">
                    {Object.entries(bulkUrls).map(([env, urls]) => (
                      <div key={env} className="text-sm">
                        <span className="font-medium text-blue-800 capitalize">{env}:</span>
                        <span className="text-blue-700 ml-2">{urls.length} URLs</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              {Object.keys(bulkUrls).length > 0 && (
                <div className="flex justify-center pt-4">
                  {hasWritePermission17 ? (
                    <ModernButton
                      type="button"
                      variant="primary"
                      size="lg"
                      icon={PlayCircle}
                      onClick={startScanningBulkUrls}
                      loading={isScanning}
                    >
                      Start Bulk Scan
                    </ModernButton>
                  ) : (
                    <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                      🔒 You have read-only access. Contact your admin to enable scanning.
                    </p>
                  )}
                </div>
              )}
            </div>
          </ModernCard>
        )}

        {/* Info Section */}
        <div className="mt-8">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">Important Notes:</p>
                <ul className="list-disc list-inside space-y-1 text-amber-700">
                  <li>Enter URLs with or without protocol (http:// or https://) - protocol will be removed automatically</li>
                  <li>Bulk scans require Excel files with columns: Url Address, Parent Website, Environment</li>
                  <li>Valid environments are: prod, dev, qa, or uat</li>
                  <li>Scan results will be available in the Scan List page</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default connect(null, { setBreadcrumbItems })(StartAScan)
